/**
 * Vocabulary API Service
 * Provides access to CSI division vocabulary definitions for fact extraction
 */

import { USE_MOCK_DATA } from './mockData';
import api from './api';

// Mock vocabulary data for development
const mockVocabulary = {
  3: {
    title: 'Concrete',
    fields: [
      {
        fieldName: 'foundation_type',
        fieldType: 'enum',
        displayName: 'Foundation Type',
        description: 'Type of foundation system',
        enumValues: ['slab_on_grade', 'spread_footing', 'mat', 'drilled_pier', 'driven_pile', 'continuous_footing'],
        importanceWeight: 0.95,
      },
      {
        fieldName: 'concrete_strength_psi',
        fieldType: 'enum',
        displayName: 'Concrete Strength',
        description: 'Concrete compressive strength in PSI',
        enumValues: ['3000', '4000', '5000', '6000'],
        importanceWeight: 0.85,
      },
      {
        fieldName: 'slab_thickness_in',
        fieldType: 'range',
        displayName: 'Slab Thickness',
        description: 'Thickness of slab-on-grade',
        rangeMin: 4,
        rangeMax: 24,
        rangeUnit: 'inches',
        importanceWeight: 0.80,
      },
      {
        fieldName: 'foundation_depth_ft',
        fieldType: 'range',
        displayName: 'Foundation Depth',
        description: 'Depth of foundation below grade',
        rangeMin: 2,
        rangeMax: 100,
        rangeUnit: 'feet',
        importanceWeight: 0.75,
      },
      {
        fieldName: 'elevated_deck_sqft',
        fieldType: 'range',
        displayName: 'Elevated Deck Area',
        description: 'Square footage of elevated concrete decks',
        rangeMin: 0,
        rangeMax: 500000,
        rangeUnit: 'SF',
        importanceWeight: 0.70,
      },
    ],
  },
  31: {
    title: 'Earthwork',
    fields: [
      {
        fieldName: 'soil_type',
        fieldType: 'enum',
        displayName: 'Soil Type',
        description: 'Primary soil classification',
        enumValues: ['clay', 'sand', 'gravel', 'rock', 'organic', 'mixed'],
        importanceWeight: 0.90,
      },
      {
        fieldName: 'rock_encountered',
        fieldType: 'boolean',
        displayName: 'Rock Encountered',
        description: 'Whether rock was encountered during excavation',
        importanceWeight: 0.85,
      },
      {
        fieldName: 'soil_bearing_capacity_psf',
        fieldType: 'range',
        displayName: 'Soil Bearing Capacity',
        description: 'Soil bearing capacity in PSF',
        rangeMin: 500,
        rangeMax: 10000,
        rangeUnit: 'PSF',
        importanceWeight: 0.80,
      },
      {
        fieldName: 'excavation_depth_ft',
        fieldType: 'range',
        displayName: 'Excavation Depth',
        description: 'Maximum excavation depth',
        rangeMin: 0,
        rangeMax: 50,
        rangeUnit: 'feet',
        importanceWeight: 0.75,
      },
      {
        fieldName: 'hazmat_types',
        fieldType: 'list',
        displayName: 'Hazmat Types',
        description: 'Types of hazardous materials present',
        listValues: ['asbestos', 'lead_paint', 'PCB', 'mold', 'UST', 'contaminated_soil'],
        importanceWeight: 0.70,
      },
    ],
  },
  1: {
    title: 'General Requirements',
    fields: [
      {
        fieldName: 'prevailing_wage',
        fieldType: 'boolean',
        displayName: 'Prevailing Wage',
        description: 'Whether prevailing wage rates apply',
        importanceWeight: 0.95,
      },
      {
        fieldName: 'working_hours_restriction',
        fieldType: 'enum',
        displayName: 'Working Hours',
        description: 'Work schedule restrictions',
        enumValues: ['standard', 'extended', '24-7', 'restricted'],
        importanceWeight: 0.80,
      },
      {
        fieldName: 'safety_requirements',
        fieldType: 'list',
        displayName: 'Safety Requirements',
        description: 'Special safety requirements',
        listValues: ['OSHA_30', 'fall_protection', 'confined_space', 'hot_work', 'lockout_tagout'],
        importanceWeight: 0.75,
      },
    ],
  },
  2: {
    title: 'Existing Conditions',
    fields: [
      {
        fieldName: 'hazmat_present',
        fieldType: 'boolean',
        displayName: 'Hazmat Present',
        description: 'Whether hazardous materials are present',
        importanceWeight: 0.90,
      },
      {
        fieldName: 'demolition_sqft',
        fieldType: 'range',
        displayName: 'Demolition Area',
        description: 'Area of demolition required',
        rangeMin: 0,
        rangeMax: 1000000,
        rangeUnit: 'SF',
        importanceWeight: 0.75,
      },
      {
        fieldName: 'survey_types',
        fieldType: 'list',
        displayName: 'Survey Types',
        description: 'Types of surveys conducted',
        listValues: ['topographic', 'boundary', 'environmental', 'geotechnical', 'utility'],
        importanceWeight: 0.70,
      },
    ],
  },
  5: {
    title: 'Metals',
    fields: [
      {
        fieldName: 'structural_steel_tons',
        fieldType: 'range',
        displayName: 'Structural Steel',
        description: 'Tonnage of structural steel',
        rangeMin: 0,
        rangeMax: 10000,
        rangeUnit: 'tons',
        importanceWeight: 0.90,
      },
      {
        fieldName: 'connection_type',
        fieldType: 'enum',
        displayName: 'Connection Type',
        description: 'Primary steel connection type',
        enumValues: ['bolted', 'welded', 'moment_frame', 'braced_frame'],
        importanceWeight: 0.80,
      },
      {
        fieldName: 'fireproofing_type',
        fieldType: 'enum',
        displayName: 'Fireproofing Type',
        description: 'Type of fireproofing for steel',
        enumValues: ['spray_applied', 'intumescent', 'board', 'none'],
        importanceWeight: 0.75,
      },
    ],
  },
  23: {
    title: 'HVAC',
    fields: [
      {
        fieldName: 'hvac_system_type',
        fieldType: 'enum',
        displayName: 'HVAC System Type',
        description: 'Primary HVAC system type',
        enumValues: ['VAV', 'VRF', 'split_system', 'packaged_rooftop', 'chilled_water', 'geothermal'],
        importanceWeight: 0.95,
      },
      {
        fieldName: 'cooling_tons',
        fieldType: 'range',
        displayName: 'Cooling Capacity',
        description: 'Total cooling capacity',
        rangeMin: 0,
        rangeMax: 5000,
        rangeUnit: 'tons',
        importanceWeight: 0.85,
      },
      {
        fieldName: 'ahu_count',
        fieldType: 'range',
        displayName: 'AHU Count',
        description: 'Number of air handling units',
        rangeMin: 0,
        rangeMax: 100,
        rangeUnit: 'units',
        importanceWeight: 0.70,
      },
    ],
  },
  26: {
    title: 'Electrical',
    fields: [
      {
        fieldName: 'service_voltage',
        fieldType: 'enum',
        displayName: 'Service Voltage',
        description: 'Main electrical service voltage',
        enumValues: ['120/208V', '277/480V', '480V', '4160V', '13.8kV'],
        importanceWeight: 0.90,
      },
      {
        fieldName: 'service_amps',
        fieldType: 'range',
        displayName: 'Service Amperage',
        description: 'Main service amperage',
        rangeMin: 100,
        rangeMax: 10000,
        rangeUnit: 'amps',
        importanceWeight: 0.85,
      },
      {
        fieldName: 'lighting_type',
        fieldType: 'enum',
        displayName: 'Lighting Type',
        description: 'Primary lighting technology',
        enumValues: ['LED', 'fluorescent', 'HID', 'incandescent'],
        importanceWeight: 0.70,
      },
    ],
  },
};

/**
 * Vocabulary API that automatically uses mock or real data
 */
export const vocabularyApi = {
  /**
   * Get all vocabulary definitions grouped by CSI division
   * @returns {Promise<Object>} Vocabulary grouped by division number
   */
  getVocabulary: async () => {
    if (USE_MOCK_DATA) {
      return mockVocabulary;
    }

    const response = await api.get('/vocabulary');
    return response.data || response;
  },

  /**
   * Get vocabulary for a specific CSI division
   * @param {number} divisionNumber - CSI division number (e.g., 3 for Concrete)
   * @returns {Promise<{title: string, fields: Array}>}
   */
  getVocabularyForDivision: async (divisionNumber) => {
    if (USE_MOCK_DATA) {
      return mockVocabulary[divisionNumber] || { title: `Division ${divisionNumber}`, fields: [] };
    }

    const response = await api.get(`/vocabulary/${divisionNumber}`);
    return response.data || response;
  },
};

export default vocabularyApi;
